import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", company: "", email: "", phone: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const set = (k) => (e) => setForm({ ...form, [k]: k === "phone" ? formatPhone(e.target.value) : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (!/^\d{3}-\d{3}-\d{4}$/.test(form.phone)) throw new Error("Enter a valid 10-digit phone number, for example 347-445-6381");
      await signup(form);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-16 order-2 lg:order-1 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="signup-form">
          <Link to="/" data-testid="signup-mobile-brand-link" className="lg:hidden block mb-8 h-16 w-40"><BrandLogo location="signup-mobile" className="invert" /></Link>
          <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-slate-500">Create account</span>
          <h1 className="font-display text-5xl tracking-tight mt-3">Join the studio.</h1>
          <p className="text-slate-500 mt-3 text-sm">Create an account with your email to track shoots and download media.</p>

          {error && <div data-testid="signup-error" className="mt-6 text-sm text-destructive bg-destructive/5 border border-destructive/20 px-4 py-3">{error}</div>}

          <div className="mt-8 space-y-5">
            <div className="grid grid-cols-2 gap-4"><div><Label htmlFor="first-name" className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">First name</Label><Input id="first-name" data-testid="signup-first-name-input" value={form.first_name} onChange={set("first_name")} autoComplete="given-name" required className="mt-2 rounded-none border-slate-300" /></div><div><Label htmlFor="last-name" className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Last name</Label><Input id="last-name" data-testid="signup-last-name-input" value={form.last_name} onChange={set("last_name")} autoComplete="family-name" required className="mt-2 rounded-none border-slate-300" /></div></div>
            <div>
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Email</Label>
              <Input id="email" data-testid="signup-email-input" type="email" value={form.email} onChange={set("email")} autoComplete="email" required className="mt-2 rounded-none border-slate-300" />
            </div>
            <div>
              <Label htmlFor="company" className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Brokerage / company <span className="normal-case tracking-normal">(optional)</span></Label>
              <Input id="company" data-testid="signup-company-input" value={form.company} onChange={set("company")} autoComplete="organization" className="mt-2 rounded-none border-slate-300" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Phone</Label>
              <Input id="phone" data-testid="signup-phone-input" type="tel" inputMode="numeric" placeholder="347-445-6381" value={form.phone} onChange={set("phone")} autoComplete="tel-national" pattern="\d{3}-\d{3}-\d{4}" required className="mt-2 rounded-none border-slate-300" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Password</Label>
              <div className="relative mt-2">
                <Input id="password" data-testid="signup-password" type={showPassword ? "text" : "password"} value={form.password} onChange={set("password")} autoComplete="new-password" required minLength={6} className="rounded-none border-slate-300 pr-12" />
                <button
                  type="button"
                  data-testid="signup-password-visibility-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-500 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-900"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>

          <Button data-testid="signup-submit-button" type="submit" disabled={loading} className="w-full mt-8 rounded-none bg-slate-900 hover:bg-slate-800 text-slate-50 py-6">
            {loading ? "Creating..." : "Create account"}
          </Button>
          <p className="text-sm text-slate-500 mt-6 text-center">
            Already have an account? <Link to="/login" data-testid="signup-to-login" className="text-foreground underline underline-offset-4">Sign in</Link>
          </p>
        </form>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2 group overflow-hidden bg-slate-900">
        <img src="https://images.unsplash.com/photo-1757361652977-218a1173e8d6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100" alt="" className="absolute inset-0 w-full h-full object-cover zoom-slow opacity-90" />
        <div className="absolute inset-0 bg-slate-900/30" />
        <Link to="/" data-testid="signup-brand-link" className="absolute top-10 right-10 h-20 w-48 bg-white p-3 shadow-lg">
          <BrandLogo location="signup" className="invert" />
        </Link>
      </div>
    </div>
  );
}
